import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, CalendarDays, MailCheck, PhoneCall, ArrowRight, Loader2 } from "lucide-react";
import { money } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  {
    icon: MailCheck,
    title: "Your receipt is on its way",
    desc: "A confirmation email with your payment details has been sent to your inbox. Keep it for your records.",
  },
  {
    icon: PhoneCall,
    title: "Zecharias reaches out within 1 business day",
    desc: "He will personally email you to gather what's needed and map out your engagement — no forms, no runaround.",
  },
  {
    icon: CalendarDays,
    title: "Schedule your kickoff call",
    desc: "Pick a time that suits you on the calendar — free, 30 minutes, Monday to Friday, 9 AM – 5 PM Pacific.",
  },
];

export default function ThankYou() {
  const [receipt, setReceipt] = useState(null);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [stripeState, setStripeState] = useState(sessionId ? "checking" : null);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStripeState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.status)) {
          setStripeState("failed");
          return;
        }
      } catch {
        // keep polling
      }
      if (attempts < 10) timer = setTimeout(poll, 2000);
      else setStripeState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      const raw = sessionStorage.getItem("bc_last_receipt");
      if (raw) setReceipt(JSON.parse(raw));
    } catch {
      // ignore malformed storage
    }
  }, []);

  return (
    <div className="min-h-screen bg-pine px-5 py-16 md:py-24" data-testid="thank-you-page">
      <div className="mx-auto max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="border border-bone/10 bg-forest/30 p-8 text-center md:p-14"
        >
          <img src="/logo-gold.png" alt="BookCare LLC" className="mx-auto mb-8 h-24 w-24 object-contain" />
          <CheckCircle2 size={40} className="mx-auto mb-6 text-terra" />
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.35em] text-terra">Payment received</p>
          <h1 className="mb-4 font-serif text-4xl font-medium leading-tight text-bone md:text-5xl" data-testid="thank-you-heading">
            Thank you — <span className="italic text-terra">you're in good hands.</span>
          </h1>

          {stripeState === "checking" && (
            <p className="mt-6 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/60" data-testid="stripe-confirming">
              <Loader2 size={13} className="animate-spin text-terra" /> Confirming your card payment…
            </p>
          )}
          {stripeState === "paid" && (
            <p className="mt-6 inline-flex items-center gap-2 border border-terra/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-terra" data-testid="stripe-confirmed">
              <CheckCircle2 size={13} /> Card payment confirmed
            </p>
          )}
          {(stripeState === "pending" || stripeState === "failed") && (
            <p className="mt-6 max-w-md text-sm text-terra" data-testid="stripe-pending">
              {stripeState === "failed"
                ? "This checkout session did not complete — no charge was made. Please try again or email Info@bookcarellc.com."
                : "Your payment is still being confirmed by Stripe — we will email your receipt as soon as it clears."}
            </p>
          )}

          {receipt && (
            <div className="mx-auto mb-2 mt-8 max-w-md border border-bone/15 px-6 py-5" data-testid="thank-you-receipt">
              <p className="font-serif text-3xl font-semibold text-bone">
                {money(receipt.amount)}
                {receipt.type === "subscribed" && (
                  <span className="text-base font-normal text-bone/70"> / month × {receipt.cycles}</span>
                )}
              </p>
              <p className="mt-2 text-sm text-bone/60">{receipt.label}</p>
            </div>
          )}

          <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-bone/60">
            Your payment went through securely with PayPal. Here's exactly what happens next:
          </p>

          <div className="mt-10 flex flex-col gap-4 text-left">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.15, duration: 0.6 }}
                className="flex items-start gap-4 border border-bone/10 bg-pine/40 p-5"
                data-testid={`thank-you-step-${i + 1}`}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-terra/40 text-terra">
                  <s.icon size={16} />
                </span>
                <span>
                  <span className="mb-1 block font-serif text-lg font-semibold text-bone">
                    {i + 1}. {s.title}
                  </span>
                  <span className="block text-xs leading-relaxed text-bone/60">{s.desc}</span>
                </span>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <a
              href="/#booking"
              data-testid="thank-you-calendar-btn"
              className="inline-flex items-center justify-center gap-2 border border-terra bg-terra px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-terra"
            >
              <CalendarDays size={14} /> Schedule your kickoff call
            </a>
            <Link
              to="/"
              data-testid="thank-you-home-btn"
              className="inline-flex items-center justify-center gap-2 border border-bone/25 px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-bone/70 transition-colors duration-300 hover:border-bone hover:text-bone"
            >
              Back to website <ArrowRight size={14} />
            </Link>
          </div>

          <p className="mt-10 border-t border-bone/10 pt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-bone/40">
            Questions about your payment? Email{" "}
            <a href="mailto:Info@bookcarellc.com" className="text-terra underline-offset-4 hover:underline">Info@bookcarellc.com</a>{" "}
            or call 425-345-9757 — we reply fast.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
